import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Clinic {
  id: number;
  name: string;
  address: string;
  city: string;
  doctor: string;
  phone: string;
  lat: number;
  lng: number;
  certified: boolean;
  cases: number;
}

const FindDoctor = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);

  const clinics: Clinic[] = [
    {
      id: 1,
      name: '北京儿童口腔医院',
      address: '北京市朝阳区建国路88号',
      city: '北京',
      doctor: '张医生',
      phone: '010-12345678',
      lat: 39.9042,
      lng: 116.4074,
      certified: true,
      cases: 156
    },
    {
      id: 2,
      name: '上海微笑口腔诊所',
      address: '上海市浦东新区世纪大道1000号',
      city: '上海',
      doctor: '李医生',
      phone: '021-87654321',
      lat: 31.2304,
      lng: 121.4737,
      certified: true,
      cases: 203
    },
    {
      id: 3,
      name: '广州天河口腔中心',
      address: '广州市天河区天河路123号',
      city: '广州',
      doctor: '王医生',
      phone: '020-98765432',
      lat: 23.1291,
      lng: 113.2644,
      certified: true,
      cases: 178
    },
    {
      id: 4,
      name: '深圳南山儿童齿科',
      address: '深圳市南山区科技园南路56号',
      city: '深圳',
      doctor: '刘医生',
      phone: '0755-23456789',
      lat: 22.5431,
      lng: 114.0579,
      certified: true,
      cases: 189
    },
    {
      id: 5,
      name: '成都锦江口腔医院',
      address: '成都市锦江区红星路二段88号',
      city: '成都',
      doctor: '陈医生',
      phone: '028-34567890',
      lat: 30.5728,
      lng: 104.0668,
      certified: true,
      cases: 142
    },
    {
      id: 6,
      name: '杭州西湖齿科诊所',
      address: '杭州市西湖区文三路168号',
      city: '杭州',
      doctor: '赵医生',
      phone: '0571-45678901',
      lat: 30.2741,
      lng: 120.1551,
      certified: true,
      cases: 167
    }
  ];

  const filteredClinics = clinics.filter(clinic =>
    clinic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    clinic.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    clinic.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleNavigation = (clinic: Clinic) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinic.address)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">
            寻找身边的专业医生
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            全国数百家合作诊所，认证医生为您的孩子提供专业矫治服务
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Search & List */}
            <div className="space-y-6">
              {/* Search Bar */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="relative">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="搜索城市或诊所名称..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-14 pr-6 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Clinic List */}
              <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
                {filteredClinics.map(clinic => (
                  <div
                    key={clinic.id}
                    className={`bg-white rounded-2xl shadow-lg p-6 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                      selectedClinic?.id === clinic.id ? 'ring-4 ring-blue-600' : ''
                    }`}
                    onClick={() => setSelectedClinic(clinic)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                          {clinic.name}
                        </h3>
                        {clinic.certified && (
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                            <i className="ri-verified-badge-fill"></i>
                            官方认证
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600">{clinic.cases}</div>
                        <div className="text-sm text-gray-500">成功案例</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <i className="ri-map-pin-line text-xl text-red-500 mt-1 w-6 h-6 flex items-center justify-center"></i>
                        <span className="text-gray-700 flex-1">{clinic.address}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="ri-user-star-line text-xl text-orange-500 w-6 h-6 flex items-center justify-center"></i>
                        <span className="text-gray-700">
                          <strong>认证医生：</strong>{clinic.doctor}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="ri-phone-line text-xl text-green-600 w-6 h-6 flex items-center justify-center"></i>
                        <a href={`tel:${clinic.phone}`} className="text-blue-600 hover:underline font-medium">
                          {clinic.phone}
                        </a>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigation(clinic);
                        }}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-navigation-line text-xl"></i>
                        一键导航
                      </button>
                    </div>
                  </div>
                ))}

                {filteredClinics.length === 0 && (
                  <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                    <i className="ri-map-pin-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-xl text-gray-500">未找到相关诊所</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Map */}
            <div className="lg:sticky lg:top-32 h-[800px]">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full">
                <div className="h-full relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509374!2d144.95373631531654!3d-37.817209979751654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65d4c2b349649%3A0xb6899234e561db11!2sEnvato!5e0!3m2!1sen!2sau!4v1620000000000!5m2!1sen!2sau"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    title="诊所地图"
                  ></iframe>
                  
                  {selectedClinic && (
                    <div className="absolute bottom-6 left-6 right-6 bg-white rounded-xl shadow-2xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="text-xl font-bold text-gray-900 flex-1">
                          {selectedClinic.name}
                        </h4>
                        <button
                          onClick={() => setSelectedClinic(null)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <i className="ri-close-line text-2xl"></i>
                        </button>
                      </div>
                      <p className="text-gray-600 mb-4">{selectedClinic.address}</p>
                      <button
                        onClick={() => handleNavigation(selectedClinic)}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-navigation-line"></i>
                        导航到此诊所
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 px-6 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              为什么选择我们的合作诊所
            </h2>
            <p className="text-xl text-gray-600">
              严格筛选，专业认证，为您的孩子提供最优质的矫治服务
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">官方认证</h3>
              <p className="text-gray-600 leading-relaxed">
                所有合作医生均经过严格培训和认证，确保专业水准
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-4xl text-orange-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">全程支持</h3>
              <p className="text-gray-600 leading-relaxed">
                从初诊到治疗结束，提供全程专业指导和技术支持
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-user-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">就近选择</h3>
              <p className="text-gray-600 leading-relaxed">
                覆盖全国主要城市，让您轻松找到身边的专业诊所
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FindDoctor;
